import { MessageKey } from '../../i18n';
import { RING_DRILLS } from '../../sim/levels/ring';
import { DRILL_CONFIG } from '../config';
import { BaseDrill, DrillEvent, DrillResult, Stars, round } from '../drill';
import { FrameState } from '../frame';

const C = DRILL_CONFIG.figure8;
const [C0, C1] = RING_DRILLS.figure8.cones as [readonly [number, number], readonly [number, number]];
const MID = { x: (C0[0] + C1[0]) / 2, z: (C0[1] + C1[1]) / 2 };
const AXIS = (() => {
  const dx = C1[0] - C0[0], dz = C1[1] - C0[1], l = Math.hypot(dx, dz);
  return { x: dx / l, z: dz / l };
})();

const wrapAngle = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));

/**
 * Drill 3: two figure eights round two cones, standing on the straights and seated in the turns.
 * A lobe is one stay on one cone's side of the crossing (entered `enterSide` past it, left when crossing back).
 * The cone counts as rounded when the rider's winding angle about it during the lobe reaches `minWinding`.
 */
export class Figure8Drill extends BaseDrill {
  readonly id = 'figure8' as const;
  readonly start = 'figure8';
  protected readonly timeLimit = C.timeLimit;
  lobes = 0;
  missed = 0;
  side: -1 | 0 | 1 = 0;
  winding = 0;
  private lastAngle = 0;
  stanceOk = 0;
  stanceCounted = 0;
  private wrongFor = 0;

  protected shouldStart(f: FrameState) { return f.input.throttle > 0; }

  protected metrics() {
    return {
      time: round(this.elapsed),
      missedCones: this.missed,
      stance: this.stanceCounted ? round((100 * this.stanceOk) / this.stanceCounted, 0) : 0,
    };
  }

  private elapsed = 0;

  private cone(side: 1 | -1) { return side > 0 ? C1 : C0; }

  protected track(f: FrameState, events: DrillEvent[]): DrillResult | null {
    const b = f.bike;
    this.elapsed = f.time - this.startTime;
    const px = b.position.x - MID.x, pz = b.position.z - MID.z;
    if (Math.hypot(px, pz) > C.areaRadius) return this.fail('result.reason.leftArea');

    const along = px * AXIS.x + pz * AXIS.z;
    if (this.side !== 0 && along * this.side < 0) {
      // Crossed back over the crossing: the lobe is over.
      this.lobes++;
      if (Math.abs(this.winding) < C.minWinding) {
        this.missed++;
        events.push({ type: 'missedCone', cone: this.side > 0 ? 1 : 0 });
      }
      this.side = 0;
      if (this.lobes >= C.lobes) return this.score();
    }
    if (this.side === 0 && Math.abs(along) > C.enterSide) {
      this.side = along > 0 ? 1 : -1;
      this.winding = 0;
      const c = this.cone(this.side);
      this.lastAngle = Math.atan2(b.position.z - c[1], b.position.x - c[0]);
    } else if (this.side !== 0) {
      const c = this.cone(this.side);
      const a = Math.atan2(b.position.z - c[1], b.position.x - c[0]);
      this.winding += wrapAngle(a - this.lastAngle);
      this.lastAngle = a;
    }

    const yaw = Math.abs(b.angularVelocity.y);
    const expected = yaw > C.turnYaw ? 'seated' : yaw < C.straightYaw ? 'standing' : null;
    if (expected) {
      const standingNow = f.input.autoPosture ? f.bike.rider.stand > 0.5 : f.input.standing;
      const ok = standingNow === (expected === 'standing');
      this.stanceCounted++;
      if (ok) { this.stanceOk++; this.wrongFor = 0; } else {
        this.wrongFor += f.dt;
        if (this.wrongFor >= C.stanceGrace) {
          events.push({ type: 'wrongStance', expected });
          this.wrongFor = 0;
        }
      }
    }
    return null;
  }

  private score(): DrillResult {
    const stance = this.stanceCounted ? this.stanceOk / this.stanceCounted : 0;
    const t3 = C.tiers[3], t2 = C.tiers[2];
    const stars: Stars = this.missed <= t3.maxMissed && stance >= t3.minStance ? 3
      : this.missed <= t2.maxMissed && stance >= t2.minStance ? 2 : 1;
    const reasons: MessageKey[] = [];
    if (this.missed > t3.maxMissed) reasons.push('result.reason.missedCone');
    if (stance < t3.minStance) reasons.push('result.reason.stance');
    return { stars, metrics: this.metrics(), reasons };
  }
}
